<template>
  <div v-if="book" class="book-detail-container">
    <div class="book-header">
      <div class="book-cover-container">
        <img :src="book.cover || defaultCover" alt="Book Cover" class="book-cover" />
        <!-- Overlay untuk judul buku -->
        <div class="book-title-overlay">
          <h2>{{ book.title }}</h2>
        </div>
      </div>
      <div class="book-info">
        <h2>{{ book.title }}</h2>
        <p><font-awesome-icon :icon="['fas', 'user']" /> {{ book.author?.name || 'Unknown' }}
        &nbsp; <span><font-awesome-icon :icon="['fas', 'eye']" /> {{ book.read_count }}</span> </p>
        <hr class="chapter-divider" />
        <p v-html="book.synopsis" class="book-synopsis"></p>
        
        <!-- Disclaimer khusus untuk buku fiksi -->
        <p v-if="book.fiction" class="book-disclaimer">
          Penafian (disclaimer): Karya fiksi ini sepenuhnya merupakan hasil imajinasi penulis. Semua karakter, peristiwa, lokasi, organisasi, dan dialog yang terdapat dalam cerita ini adalah rekaan semata dan tidak dimaksudkan untuk mencerminkan kejadian nyata atau individu yang sebenarnya. Konten dilindungi hak cipta. Tidak diperkenankan untuk menyalin atau mendistribusikan.
          <router-link to="/disclaimer" class="disclaimer-link">[selengkapnya]</router-link>
        </p>
        <p v-else class="book-disclaimer">
          Penafian (disclaimer): Buku ini merupakan karya non-fiksi yang berisi informasi dan pandangan pribadi penulis. Konten buku ini dilindungi hak cipta. Tidak diperkenankan untuk menyalin atau mendistribusikan.
        </p>
        <div class="book-actions">
          <AddToCollectionButton :book-id="book.id" :book-slug="book.slug" />
        </div>
      </div>
    </div>

    <!-- Chapters List -->
    <div class="book-chapters" v-if="book.chapters?.length">
      <h4>Daftar Isi</h4>
      <hr class="chapter-divider" />
      <ul>
        <li v-for="chapter in book.chapters" :key="chapter.id" class="chapter-item">
          <font-awesome-icon :icon="['fas', 'bars']" class="chapter-icon" />

          <!-- Chapter Access Control -->
          <router-link
            v-if="canAccessChapter(chapter)"
            :to="`/books/${book.slug}/chapters/${chapter.slug}`"
          >
            {{ chapter.title }}
          </router-link>
          <span v-else @click="showLoginModal">
            <span class="restricted-chapter">{{ chapter.title }}</span> <span class="text-warning"><font-awesome-icon :icon="['fas', 'lock']" /></span>
          </span>

          <p class="chapter-meta text-secondary">
            <font-awesome-icon :icon="['fas', 'calendar-alt']" /> {{ formatDate(chapter.updated_date) }} |
            <font-awesome-icon :icon="['fas', 'file-word']" /> {{ chapter.word_count }} kata |
            <font-awesome-icon :icon="['fas', 'clock']" /> {{ chapter.estimated_read_time }} menit
          </p>
          <hr class="chapter-divider" />
        </li>
      </ul>
    </div>

    <p v-else class="no-chapters">Tidak ada bab yang tersedia untuk buku ini.</p>

    <!-- NEW: Reviews Section -->
    <div class="book-reviews">
      <h4>Ulasan</h4>
      <hr class="chapter-divider" />
      
      <!-- Review Form for logged-in users -->
      <div v-if="isLoggedIn" class="review-form-container">
        <h5>Tulis Ulasan</h5>
        <form @submit.prevent="submitReview" class="review-form">
          <div class="rating-select">
            <label>Rating:</label>
            <div class="star-rating">
              <span v-for="star in 5" :key="star" 
                    @click="newReview.rating = star"
                    :class="{ 'active': star <= newReview.rating, 'star-icon': true }">
                <font-awesome-icon :icon="['fas', 'star']" />
              </span>
            </div>
          </div>
          <div class="form-group">
            <label for="reviewComment">Ulasanmu:</label>
            <textarea id="reviewComment" v-model="newReview.comment" 
                      class="form-control" rows="3" placeholder="Bagikan pendapatmu tentang buku ini..."></textarea>
          </div>
          <button type="submit" class="btn btn-primary mt-2" :disabled="isSubmitting">
            <font-awesome-icon v-if="isSubmitting" :icon="['fas', 'spinner']" class="fa-spin" />
            {{ isSubmitting ? 'Mengirim...' : 'Kirim Ulasan' }}
          </button>
        </form>
      </div>
      
      <!-- Message for non-logged in users -->
      <div v-else class="login-to-review">
        <p>Silakan <span @click="showLoginModal" class="login-link">login</span> untuk mengirim ulasan</p>
      </div>
      
      <!-- Display existing reviews -->
      <div class="reviews-list" v-if="book.reviews && book.reviews.length">
        <h5>{{ book.reviews.length }} Ulasan</h5>
        <div v-for="review in book.reviews" :key="review.id" class="review-item">
          <div class="review-header">
            <div class="review-stars">
              <font-awesome-icon v-for="star in 5" :key="star"
                                :icon="['fas', 'star']"
                                :class="{ 'active': star <= review.rating }"/>
            </div>
            <div class="review-avatar">
              <img v-if="review.avatar" :src="review.avatar" alt="User Avatar" class="avatar" />
              <font-awesome-icon v-else :icon="['fas', 'user']" class="default-avatar" />
            </div>
            <span class="review-user">{{ review.user }}</span>
            <span class="review-date">{{ formatDate(review.created_at) }}</span>
          </div>
          <p class="review-comment">{{ review.comment }}</p>
        </div>
      </div>
      <div v-else-if="book.reviews && book.reviews.length === 0" class="no-reviews">
        <p>Belum ada ulasan. Kirim ulasanmu sebagai pengulas pertama!</p>
      </div>
    </div>


    <!-- Login Modal with LoginForm Component -->
    <div v-if="isLoginModalVisible" class="modal-overlay">
      <div class="modal-content">
        <button @click="isLoginModalVisible = false" class="btn-close">
          <font-awesome-icon :icon="['fas', 'times']" class="btn-close" />
        </button>
        <h3><i class="fas fa-lock"></i> Akses Terbatas</h3>
        <p>Silakan login untuk membaca bab ini.</p>
        
        <!-- Embedded Login Form Component -->
        <LoginForm @login-success="handleLoginSuccess" :redirectHome="false" />
      </div>
    </div>
  </div>

  <div v-else class="loading">
    <p><i class="fas fa-spinner fa-spin"></i> Loading...</p>
  </div>
</template>
  
<script>
import axios from "axios";
import LoginForm from "@/components/LoginForm.vue"; 
import Swal from 'sweetalert2';
import { useHead } from '@unhead/vue';
import { ref, watch } from 'vue';
import AddToCollectionButton from "@/components/AddToCollectionButton.vue";

export default {
  name: "BookDetail",
  components: {
    LoginForm,
    AddToCollectionButton
  },
  setup() {
    const book = ref({
      chapters: [],
      title: '',
      author: { name: '' }
    });

    // Function to strip HTML tags for meta description
    const stripHtml = (html) => {
      if (!html) return '';
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      return tempDiv.textContent || tempDiv.innerText || '';
    };

    // Update meta tags whenever book data changes
    watch(() => book.value, (newBook) => {
      if (newBook && newBook.title) {
        // Extract a clean description from synopsis
        const plainSynopsis = stripHtml(newBook.synopsis);
        const shortDescription = plainSynopsis.length > 160 
          ? plainSynopsis.substring(0, 157) + '...' 
          : plainSynopsis;

        // Set meta tags
        useHead({
          title: `${newBook.title} - HobiBaca`,
          meta: [
            { name: 'description', content: shortDescription },
            { name: 'keywords', content: `${newBook.title}, ${newBook.author?.name || 'buku'}, HobiBaca, buku, membaca` },
            { property: 'og:title', content: newBook.title },
            { property: 'og:description', content: shortDescription },
            { property: 'og:type', content: 'book' },
            { property: 'og:image', content: newBook.cover || 'https://via.placeholder.com/200x300?text=No+Cover' },
            { property: 'book:author', content: newBook.author?.name || 'Unknown' },
            { property: 'book:isbn', content: newBook.isbn || '' },
            { property: 'book:release_date', content: newBook.year_published?.toString() || '' },
            { name: 'twitter:card', content: 'summary_large_image' },
            { name: 'twitter:title', content: newBook.title },
            { name: 'twitter:description', content: shortDescription },
            { name: 'twitter:image', content: newBook.cover || 'https://via.placeholder.com/200x300?text=No+Cover' }
          ]
        });
      }
    }, { deep: true });

    return { book };
  },
  data() {
    return {
      defaultCover: "https://via.placeholder.com/200x300?text=No+Cover",
      isLoginModalVisible: false,
      newReview: {
        rating: 0,
        comment: ""
      },
      isSubmitting: false,
    };
  },
  computed: {
    isLoggedIn() {
      return !!localStorage.getItem("token");
    },
    userRole() {
      return localStorage.getItem("role") || "guest";
    }
  },
  async created() {
    await this.fetchBookDetails();
  },
  methods: {
    async fetchBookDetails() {
      const slug = this.$route.params.slug;
      try {
        const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/api/books/${slug}/`);
        this.book = response.data || { chapters: [], reviews: [] };
      } catch (err) {
        console.error("Failed to fetch book details", err);
        this.book = { 
          title: "Buku Tidak Ditemukan", 
          read_count: 0, 
          author: { name: "Unknown" }, 
          chapters: [],
          reviews: []
        };
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },
    showLoginModal() {
      this.isLoginModalVisible = true;
    },
    handleLoginSuccess() {
      this.isLoginModalVisible = false;
      this.userRole = localStorage.getItem("role") || "guest";
      // Reload the page to reflect changes
      window.location.reload();
    },
    canAccessChapter(chapter) {
      if (chapter.order_number === 1) return true;
      if (chapter.order_number === 2 && this.isLoggedIn) return true;
      if (this.isSubscriber() || this.isAdmin()) return true;
      return false;
    },
    isSubscriber() {
      return ["subscriber_fiction", "subscriber_nonfiction", "subscriber_all"].includes(this.userRole);
    },
    isAdmin() {
      return ["curator", "finance", "superadmin"].includes(this.userRole);
    },
    async submitReview() {
      if (this.newReview.rating === 0) {
        Swal.fire({
          icon: 'info',
          text: 'Mohon isikan rating sebelum mengisi ulasan.',
        });
        return;
      }
          
      this.isSubmitting = true;
      
      try {
        const token = localStorage.getItem("token");
        
        if (!token) {
          this.showLoginModal();
          this.isSubmitting = false;
          return;
        }
        
        // Create a separate API endpoint for review creation
        const apiUrl = `${process.env.VUE_APP_API_BASE_URL}/api/books/${this.book.slug}/reviews/`;
        
        const response = await axios({
          method: 'post',
          url: apiUrl,
          data: {
            book: this.book.id,
            rating: this.newReview.rating,
            comment: this.newReview.comment
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.status === 201) {
          // Add the new review to the book's reviews
          if (!this.book.reviews) {
            this.book.reviews = [];
          }
          
          // Add the new review to the list
          this.book.reviews.push(response.data);
          
          // Reset the form
          this.newReview = {
            rating: 0,
            comment: ""
          };
          
          // Reload the book to get updated average rating
          this.fetchBookDetails();
        }
      } catch (error) {
        console.error("Error submitting review:", error);
        
        if (error.response && error.response.status === 400) {
          Swal.fire({
            icon: 'info',
            text: error.response.data.detail || 'Ulasan tidak disimpan. Kamu sudah pernah mengirim ulasan buku ini.',
          });
        } else if (error.response && error.response.status === 401) {
          // Handle expired token
          Swal.fire({
            icon: 'info',
            text: 'Sesimu sudah kadaluwarsa. Silakan login lagi',
          });
          this.showLoginModal();
        } else {
          // Handle other errors
          Swal.fire({
            icon: 'error',
            text: 'Ada kesalahan di pihak kami, saat Anda mengirim ulasan. Mohon dicoba kembali',
          });
        }
      } finally {
        this.isSubmitting = false;
      }
    }
  }
};
</script>
  
<style scoped>
.book-detail-container {
  max-width: 900px;
  margin: 40px auto;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}

.book-header {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.book-cover-container {
  position: relative;
  width: 200px;
  flex-shrink: 0;
}

.book-cover {
  width: 100%;
  height: auto;
  border-radius: 5px;
}

.book-actions {
  display: flex;
  gap: 10px;
  margin: 15px 0;
}

.book-actions button {
  background-color: #007bff;
  color: white;
  border: none;
  padding: 10px 15px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.book-title-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  padding: 10px;
  border-bottom-left-radius: 5px;
  border-bottom-right-radius: 5px;
}

.book-title-overlay h2 {
  color: white;
  font-size: 1.2rem;
  margin-bottom: 10px;
  text-align: center;
}

.book-info {
  flex: 1;
}

.book-synopsis {
  margin-top: 10px;
  color: #555;
}

/* Disclaimer styling */
.book-disclaimer {
  font-size: 12px;
  color: #777;
  margin-top: 15px;
  padding: 10px;
  background-color: #f9f9f9;
  border-left: 3px solid #ddd;
  line-height: 1.4;
}

.disclaimer-link {
  color: #666;
  text-decoration: none;
  font-style: italic;
}

.disclaimer-link:hover {
  text-decoration: underline;
  color: #444;
}

/* Chapter List */
.book-chapters {
  margin-top: 20px;
}

.book-chapters ul {
  list-style-type: none;
  padding: 0;
}

.chapter-item {
  padding: 10px 0;
}

.chapter-icon {
  font-size: 18px;
  margin-right: 8px;
}

.chapter-meta {
  font-size: 14px;
  color: #666;
  margin-left: 26px;
}

.chapter-divider {
  border: 0;
  height: 1px;
  background: #ddd;
  margin: 5px 0;
}

.no-chapters {
  color: #777;
  font-style: italic;
}

.loading {
  text-align: center;
  margin-top: 50px;
  font-size: 18px;
}

.restricted-chapter {
  cursor: pointer;

}

/* Modal Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
}

.modal-content {
  background: white;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  position: relative;
  width: 100%;
  max-width: 400px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}

.btn-close {
  position: absolute;
  top: 10px;
  right: 10px;
  background: transparent;
  border: none;
  font-size: 18px;
  cursor: pointer;
}

.btn-close i {
  color: #888;
  font-size: 20px;
}

.btn-close i:hover {
  color: #333;
}

/* Add new review-related styles */
/* Review styles */
.book-reviews {
  margin-top: 30px;
}

.review-form-container {
  background-color: #f9f9f9;
  padding: 20px;
  border-radius: 8px;
  margin-bottom: 20px;
}

.star-rating {
  display: inline-flex;
  margin-left: 10px;
}

.star-icon {
  color: #ddd;
  cursor: pointer;
  font-size: 24px;
  margin-right: 5px;
}

.star-icon.active {
  color: #ffc107;
}

.login-to-review {
  background-color: #f9f9f9;
  padding: 15px;
  text-align: center;
  border-radius: 8px;
  margin-bottom: 20px;
}

.login-link {
  color: #007bff;
  cursor: pointer;
  text-decoration: underline;
}

.review-item {
  border-bottom: 1px solid #eee;
  padding: 15px 0;
}

.review-header {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.review-stars {
  margin-right: 10px;
}

.review-stars .active {
  color: #ffc107;
}

.review-user {
  font-weight: bold;
  margin-right: 10px;
}

.review-date {
  color: #777;
  font-size: 0.9em;
}

.review-comment {
  color: #333;
  line-height: 1.5;
}

.no-reviews {
  font-style: italic;
  color: #777;
  text-align: center;
  padding: 20px;
}

.review-avatar {
  width: 20px;
  height: 20px;
  overflow: hidden;
  margin: 5px;
}

.review-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.default-avatar {
  font-size: 24px;
  color: #ccc;
}

/* Mobile Styles */
@media (max-width: 768px) {
  .book-header {
    flex-direction: column;
  }
  
  .book-cover-container {
    width: 180px;
    margin: 0 auto 20px;
  }
  
  .book-info {
    width: 100%;
    padding: 0;
  }
  
  .book-info h2 {
    text-align: center;
    margin-top: 0;
  }
  
  .book-title-overlay {
    display: none; /* Hide the overlay title on mobile as we show it below the image */
  }
  
  .chapter-meta {
    margin-left: 0;
    padding-left: 26px;
  }
  
  /* Ensure text is properly wrapped */
  .book-synopsis, .book-disclaimer {
    word-wrap: break-word;
    width: 100%;
  }
  
  /* Make modal responsive */
  .modal-content {
    width: 90%;
    padding: 20px;
  }
  
  /* Improve review form for mobile */
  .review-form-container {
    padding: 15px;
  }
  
  .review-header {
    flex-wrap: wrap;
  }
}
</style>